import {type RunSignature} from '../../../types/run';
import {type TileMediaSpec} from '../../MediaCard';

/**
 * A brief note:
 * 1. When mapping RunSignatureKey -> TileMediaSpec[], _multiple_ TileMediaSpecs share can share a run signature key
 * 2. When generating a uniq ID for a TileMediaSpec (a `TileMediaSpecKey`), we extend upon the RunSignatureKey
 * 3. Each unique TileMediaSpec might share a common filepath, e.g. when there are multiple tiles using the same image sprite
 */

const tileMediaSpecKeySeparator = '__WBMediaFetchTileMediaSpecKeySeparator__';
export type TileMediaSpecKey =
  `${TileMediaSpec['mediaType']}${typeof tileMediaSpecKeySeparator}${RunSignatureKey}${typeof tileMediaSpecKeySeparator}${TileMediaSpec['mediaKey']}${typeof tileMediaSpecKeySeparator}${TileMediaSpec['mediaStep']}${typeof tileMediaSpecKeySeparator}${TileMediaSpec['mediaIndex']}`;
export function getKeyForTileMediaSpec({
  mediaType,
  runSignature,
  mediaKey,
  mediaStep,
  mediaIndex,
}: TileMediaSpec) {
  const runSignatureKey = getKeyForRunSignature(runSignature);
  return [mediaType, runSignatureKey, mediaKey, mediaStep, mediaIndex].join(
    tileMediaSpecKeySeparator
  ) as TileMediaSpecKey;
}

const runSignatureKeySeparator = '__WBMediaFetchRunSignatureKeySeparator__';
export type RunSignatureKey =
  `${RunSignature['entityName']}${typeof runSignatureKeySeparator}${RunSignature['projectName']}${typeof runSignatureKeySeparator}${RunSignature['runName']}`;
export function getKeyForRunSignature({
  entityName,
  projectName,
  runName,
}: RunSignature) {
  return [entityName, projectName, runName].join(
    runSignatureKeySeparator
  ) as RunSignatureKey;
}

const filePathKeySeparator = '__WBMediaFetchFilePathKeySeparator__';
export type FilePathKey =
  `${RunSignatureKey}${typeof filePathKeySeparator}${string}`;
export function getKeyForFilePath(
  runSignature: RunSignature,
  filePath: string
): FilePathKey {
  return [getKeyForRunSignature(runSignature), filePath].join(
    filePathKeySeparator
  ) as FilePathKey;
}

/*
 * Helper functions to parse the keys into their constituent parts
 */
const parseRunSignatureKey = (key: RunSignatureKey) => {
  const [entityName, projectName, runName] = key.split(
    runSignatureKeySeparator
  );
  return {entityName, projectName, runName};
};

export const parseTileMediaSpecKey = (key: TileMediaSpecKey) => {
  const [mediaType, runSignatureKey, mediaKey, mediaStep, mediaIndex] =
    key.split(tileMediaSpecKeySeparator);
  const runSignature = parseRunSignatureKey(runSignatureKey as RunSignatureKey);
  return {
    mediaType,
    mediaKey,
    mediaStep: Number(mediaStep),
    mediaIndex: Number(mediaIndex),
    ...runSignature,
  };
};
